import { useEffect, useState } from "react";
import { Link, NavLink, useLocation, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Search, ShoppingBag, User, Menu, X, LogOut, ChevronDown, Heart, ChevronRight } from "lucide-react";
import { CATEGORIES } from "@/data/catalog";
import { useCart } from "@/context/CartContext";
import { useAuth } from "@/context/AuthContext";
import { useWishlist } from "@/context/WishlistContext";
import { LanguageSwitcher } from "./LanguageSwitcher";
import { MegaMenu } from "./MegaMenu";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from "@/components/ui/dropdown-menu";

export const Header = () => {
  const { t } = useTranslation();
  const { count, setOpen } = useCart();
  const { user, logout } = useAuth();
  const { count: wishCount } = useWishlist();
  const [mega, setMega] = useState(null);
  const [mobile, setMobile] = useState(false);
  const [openCat, setOpenCat] = useState(null);
  const [q, setQ] = useState("");
  const [scrolled, setScrolled] = useState(false);
  const nav = useNavigate();
  const loc = useLocation();

  useEffect(() => { setMega(null); setMobile(false); }, [loc]);
  useEffect(() => {
    const f = () => setScrolled(window.scrollY > 10);
    window.addEventListener("scroll", f);
    return () => window.removeEventListener("scroll", f);
  }, []);

  const submit = (e) => { e.preventDefault(); if (q.trim()) { nav(`/search?q=${encodeURIComponent(q.trim())}`); setQ(""); } };

  return (
    <header className={`sticky top-0 z-50 bg-white/90 backdrop-blur-xl transition-shadow ${scrolled ? "shadow-[0_10px_30px_-15px_rgba(0,0,0,0.25)]" : ""}`} onMouseLeave={() => setMega(null)}>
      <div className="bg-black text-white">
        <div className="nv-container flex h-9 items-center justify-between text-[11px] font-semibold uppercase tracking-wider">
          <div className="ml-auto flex items-center gap-2">
            <LanguageSwitcher dark />
            <span className="h-4 w-px bg-white/20" />
            {user ? (
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <button data-testid="header-user-menu" className="inline-flex items-center gap-1.5 px-2 py-1 text-white/80 hover:text-[#c6ff00]">
                    <User size={14} /> {user.firstName} <ChevronDown size={12} />
                  </button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" className="rounded-xl p-1.5">
                  <DropdownMenuItem asChild><Link to="/account" data-testid="header-account-link" className="cursor-pointer rounded-lg px-3 py-2 text-sm font-semibold focus:bg-[#c6ff00]">{t("nav.account")}</Link></DropdownMenuItem>
                  <DropdownMenuItem asChild><Link to="/wishlist" data-testid="header-wishlist-link" className="cursor-pointer rounded-lg px-3 py-2 text-sm font-semibold focus:bg-[#c6ff00]">{t("wishlist.nav")}</Link></DropdownMenuItem>
                  <DropdownMenuItem onClick={logout} data-testid="header-logout" className="cursor-pointer rounded-lg px-3 py-2 text-sm font-semibold focus:bg-[#c6ff00]"><LogOut size={14} className="mr-2" />{t("nav.logout")}</DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            ) : (
              <>
                <Link to="/login" data-testid="header-login" className="px-2 py-1 text-white/80 hover:text-[#c6ff00]">{t("nav.login")}</Link>
                <span className="text-white/30">/</span>
                <Link to="/register" data-testid="header-register" className="px-2 py-1 text-[#c6ff00] hover:text-white">{t("nav.register")}</Link>
              </>
            )}
          </div>
        </div>
      </div>

      <div className="nv-container relative flex h-[76px] items-center gap-4 sm:gap-6 lg:h-24">
        <button className="lg:hidden" onClick={() => setMobile(!mobile)} data-testid="mobile-menu-toggle" aria-label="menu">{mobile ? <X /> : <Menu />}</button>
        <Link to="/" data-testid="header-logo" className="flex shrink-0 items-center">
          <img src="/brand/logo.webp" alt="NINJAVITY" className="h-12 w-auto sm:h-14 lg:h-[66px]" />
        </Link>
        <nav className="hidden lg:flex items-center gap-8 pl-4">
          <NavLink to="/eshop" end data-testid="nav-eshop" onMouseEnter={() => setMega(null)} className={({ isActive }) => `nv-underline font-display text-sm font-bold uppercase tracking-[0.12em] ${isActive ? "text-black" : "text-black/70 hover:text-black"}`}>
            {({ isActive }) => <span data-active={isActive} className="normal-case">{t("nav.eshop")}</span>}
          </NavLink>
          {CATEGORIES.map((c) => (
            <NavLink
              key={c.id}
              to={`/c/${c.id}`}
              data-testid={`nav-${c.id}`}
              onMouseEnter={() => setMega(c.id)}
              onClick={() => setMega(null)}
              className={({ isActive }) => `nv-underline font-display text-sm font-bold uppercase tracking-[0.12em] ${isActive ? "text-black" : "text-black/70 hover:text-black"}`}
            >
              {({ isActive }) => <span data-active={isActive || mega === c.id}>{t(`nav.${c.id}`)}</span>}
            </NavLink>
          ))}
        </nav>
        <form onSubmit={submit} className="ml-auto hidden md:flex items-center">
          <div className="relative">
            <Search size={16} className="pointer-events-none absolute left-4 top-1/2 -translate-y-1/2 text-black/40" />
            <input
              data-testid="search-input"
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder={t("nav.search")}
              className="w-56 rounded-full border border-black/10 bg-[#f4f5f0] py-2.5 pl-10 pr-4 text-sm outline-none transition-[width,border-color,box-shadow] focus:w-72 focus:border-black focus:ring-2 focus:ring-[#c6ff00]"
            />
          </div>
        </form>
        <Link to="/wishlist" data-testid="wishlist-button" className="relative ml-auto md:ml-0 grid h-11 w-11 place-items-center rounded-full border border-black/10 text-black transition-[background-color,color,border-color] hover:border-black hover:bg-black hover:text-[#c6ff00]" aria-label={t("wishlist.nav")}>
          <Heart size={18} />
          {wishCount > 0 && <span data-testid="wishlist-count" className="absolute -right-1 -top-1 grid h-5 min-w-5 place-items-center rounded-full bg-[#c6ff00] px-1 text-[10px] font-bold text-black ring-2 ring-white">{wishCount}</span>}
        </Link>
        <button onClick={() => setOpen(true)} data-testid="cart-button" className="relative grid h-11 w-11 place-items-center rounded-full bg-black text-white transition-[background-color,color] hover:bg-[#c6ff00] hover:text-black" aria-label={t("nav.cart")}>
          <ShoppingBag size={18} />
          {count > 0 && <span data-testid="cart-count" className="absolute -right-1 -top-1 grid h-5 min-w-5 place-items-center rounded-full bg-[#c6ff00] px-1 text-[10px] font-bold text-black ring-2 ring-white">{count}</span>}
        </button>
        {mega && <MegaMenu catId={mega} onClose={() => setMega(null)} />}
      </div>

      {mobile && (
        <div data-testid="mobile-menu" className="lg:hidden max-h-[calc(100svh-112px)] overflow-y-auto overscroll-contain border-t border-black/10 bg-white px-4 pb-8 pt-4">
          <form onSubmit={submit} className="mb-3"><input data-testid="mobile-search-input" value={q} onChange={(e) => setQ(e.target.value)} placeholder={t("nav.search")} className="nv-input" /></form>
          <Link to="/eshop" data-testid="mobile-nav-eshop" className="flex items-center justify-between border-b border-black/5 py-4 font-display text-lg font-bold tracking-wider text-[#6f9c00]">
            {t("nav.eshop")} <ChevronRight size={18} />
          </Link>
          {CATEGORIES.map((c) => {
            const open = openCat === c.id;
            return (
              <div key={c.id} className="border-b border-black/5">
                <div className="flex items-center">
                  <Link to={`/c/${c.id}`} data-testid={`mobile-nav-${c.id}`} className="flex-1 py-4 font-display text-lg font-bold uppercase tracking-wider">{t(`nav.${c.id}`)}</Link>
                  <button type="button" data-testid={`mobile-expand-${c.id}`} aria-expanded={open} aria-label={t(`nav.${c.id}`)} onClick={() => setOpenCat(open ? null : c.id)} className={`grid h-10 w-10 place-items-center rounded-full transition-[background-color,transform] ${open ? "bg-black text-[#c6ff00] rotate-180" : "bg-[#f4f5f0]"}`}>
                    <ChevronDown size={18} />
                  </button>
                </div>
                {open && (
                  <div data-testid={`mobile-subs-${c.id}`} className="nv-fade-up flex flex-wrap gap-2 pb-4">
                    <Link to={`/c/${c.id}`} className="rounded-full bg-black px-3 py-1.5 text-xs font-semibold text-[#c6ff00]">{t("nav.all")}</Link>
                    {c.subs.map((s) => <Link key={s} to={`/c/${c.id}?sub=${s}`} className="rounded-full bg-[#f4f5f0] px-3 py-1.5 text-xs font-semibold">{t(`subs.${s}`)}</Link>)}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
    </header>
  );
};
